import { useEffect, useState } from "react";
import { Smartphone, Loader2, Sunrise, Sun, Sunset } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../../../lib/api";
import { formatPrice } from "../../../lib/formatPrice";
import { loadRazorpay } from "../../../lib/loadRazorpay";
import LabBookingLayout from "../components/LabBookingLayout";
import { useLabBooking } from "../context/LabBookingContext";

const PLATFORM_ID = 3; // WEB — see App\Http\Constants.php.
const RAZORPAY = 2; // App\Http\Constants.php.

const formatTime12h = (time) => {
  const [h, m] = time.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${String(hour12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
};

// Same morning/afternoon/evening split shown on the Flutter slot picker
// (lab_master_test_page select-slot screen) — before noon / noon to 4pm / after 4pm.
const SLOT_PERIODS = [
  { key: "morning", label: "Morning", icon: Sunrise, test: (h) => h < 12 },
  { key: "afternoon", label: "Afternoon", icon: Sun, test: (h) => h >= 12 && h < 16 },
  { key: "evening", label: "Evening", icon: Sunset, test: (h) => h >= 16 },
];

const groupSlotsByPeriod = (slots) =>
  SLOT_PERIODS.map((period) => ({
    ...period,
    slots: slots.filter((s) => period.test(Number(s.from_time.split(":")[0]))),
  })).filter((period) => period.slots.length > 0);

const ReviewOrderPage = () => {
  const navigate = useNavigate();
  const {
    user,
    patient,
    selectedLab,
    collectionType,
    location,
    logout,
    setCompletedOrder,
    clearSelection,
  } = useLabBooking();

  const testCollectionType = collectionType === "lab" ? 1 : 2;

  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [addressForm, setAddressForm] = useState({
    type: "Home",
    address_1: "",
    address_2: "",
    pincode: "",
    city: "",
    state: "",
  });

  const [slotDays, setSlotDays] = useState([]);
  const [dateIdx, setDateIdx] = useState(0);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [calculation, setCalculation] = useState(null);
  const [orderRefId, setOrderRefId] = useState(null);
  const [checking, setChecking] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);

  // Stored when Razorpay payment succeeds but recheck fails — lets user retry.
  const [pendingRecheck, setPendingRecheck] = useState(null); // { pg, rzpResponse }
  const [recheckRetries, setRecheckRetries] = useState(0);

  // Reset stale pricing and auto-fetch as soon as all booking fields are ready.
  // This eliminates the manual "Confirm Pricing" step.
  useEffect(() => {
    setCalculation(null);
    setOrderRefId(null);
    setError(null);
    if (!canCheckout || checking) return;
    handleCheckout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSlotId, selectedAddressId]); // date changes reset selectedSlotId to null, so no need to dep on date

  // Home collection needs a saved address; lab visit doesn't.
  useEffect(() => {
    if (!user || testCollectionType !== 2) return;
    api
      .post("/user/address")
      .then((res) => {
        const list = res.data?.data || [];
        setAddresses(list);
        setSelectedAddressId(list[0]?.id ?? null);
      })
      .catch(() => {});
  }, [user, testCollectionType]);

  useEffect(() => {
    if (!selectedLab?.lab?.id) return;
    setLoadingSlots(true);
    setSelectedSlotId(null);
    api
      .post("/user/lab/slot", {
        lab_id: selectedLab.lab.id,
        test_collection_type: testCollectionType,
      })
      .then((res) => {
        setSlotDays(res.data?.data || []);
        setDateIdx(0);
      })
      .catch(() => setSlotDays([]))
      .finally(() => setLoadingSlots(false));
  }, [selectedLab, testCollectionType]);

  const selectedDay = slotDays[dateIdx];

  const handleSaveAddress = () => {
    if (
      !addressForm.address_1.trim() ||
      !addressForm.address_2.trim() ||
      !addressForm.pincode.trim() ||
      !addressForm.city.trim() ||
      !addressForm.state.trim()
    ) {
      setError("All address fields are required.");
      return;
    }
    if (!location) {
      setError("Location is needed to save an address.");
      return;
    }
    setSavingAddress(true);
    setError(null);
    api
      .post("/user/address/add", {
        ...addressForm,
        latitude: location.latitude,
        longitude: location.longitude,
      })
      .then((res) => {
        if (res.data?.code === 200) {
          const created = res.data.data;
          setAddresses((prev) => [...prev, created]);
          setSelectedAddressId(created.id);
          setShowAddressForm(false);
        } else {
          setError(res.data?.message || "Couldn't save address.");
        }
      })
      .catch(() => setError("Couldn't save address."))
      .finally(() => setSavingAddress(false));
  };

  const canCheckout =
    !!patient &&
    !!selectedLab?.lab?.id &&
    !!selectedSlotId &&
    !!selectedDay?.date &&
    (testCollectionType !== 2 || !!selectedAddressId);

  const buildBookingFields = () => ({
    lab_id: selectedLab.lab.id,
    test_ids: (selectedLab.testList || []).map((t) => t.id).join(","),
    test_collection_type: testCollectionType,
    address_id: testCollectionType === 2 ? selectedAddressId : undefined,
    lab_slot_id: selectedSlotId,
    date: selectedDay.date,
    user_patient_id: patient.id,
    platform_id: PLATFORM_ID,
    user_id: user.id,
  });

  const handleCheckout = () => {
    if (!canCheckout) return;
    setChecking(true);
    setError(null);
    api
      .post("/user/lab/booking-page", buildBookingFields())
      .then((res) => {
        if (res.data?.code === 200) {
          setCalculation(res.data.data.calculation);
          setOrderRefId(res.data.data.order_ref_id);
        } else {
          setError(res.data?.message || "Couldn't calculate pricing.");
        }
      })
      .catch((err) => {
        const serverMsg = err.response?.data?.message;
        if (err.response?.status === 401) {
          logout();
          navigate("/lab-booking/login");
          return;
        }
        setError(serverMsg || "Couldn't calculate pricing. Please try again.");
        console.error("[booking-page] error:", err.response?.status, err.response?.data);
      })
      .finally(() => setChecking(false));
  };

  const handlePay = () => {
    if (!orderRefId) return;
    setPaying(true);
    setError(null);
    api
      .post("/user/lab/payment", {
        ...buildBookingFields(),
        payment_mode_id: RAZORPAY,
        order_ref_id: orderRefId,
      })
      .then(async (res) => {
        if (res.data?.code !== 200) {
          setError(res.data?.message || "Couldn't start payment.");
          setPaying(false);
          return;
        }
        const pg = res.data.data;
        try {
          await loadRazorpay();
        } catch (e) {
          console.error("[razorpay] script load failed:", e);
          setError("Payment gateway failed to load. Please check your internet connection and try again.");
          setPaying(false);
          return;
        }
        let rzp;
        try {
          rzp = new window.Razorpay({
            key: pg.key,
            amount: pg.amount,
            order_id: pg.order_id,
            name: "HealthismPlus",
            description: pg.description,
            prefill: {
              contact: pg.profile?.contact,
              email: pg.profile?.email || undefined,
            },
            handler: (response) => {
              const rzpResponse = JSON.stringify({
                paymentId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                signature: response.razorpay_signature,
              });
              api
                .post("/user/lab/payment-recheck", {
                  order_id: pg.booking_id,
                  rzp_response: rzpResponse,
                  whatsapp_notification: true,
                })
                .then((recheckRes) => {
                  if (recheckRes.data?.code === 200) {
                    setPendingRecheck(null);
                    setCompletedOrder({
                      orderId: pg.booking_id,
                      amount: calculation?.order_sub_total,
                      date: selectedDay.date,
                      time: `${selectedDay?.slot?.find((s) => s.id === selectedSlotId)?.from_time || ""} - ${selectedDay?.slot?.find((s) => s.id === selectedSlotId)?.to_time || ""}`,
                    });
                    clearSelection();
                    navigate("/lab-booking/confirmed");
                  } else {
                    // Payment succeeded on Razorpay but recheck failed — save context so user can retry.
                    setPendingRecheck({ pg, rzpResponse });
                    setRecheckRetries(0);
                    setError("Payment was captured but verification failed. Please retry below.");
                  }
                })
                .catch(() => {
                  setPendingRecheck({ pg, rzpResponse });
                  setRecheckRetries(0);
                  setError("Payment was captured but we couldn't verify it. Please retry below.");
                })
                .finally(() => setPaying(false));
            },
            modal: {
              ondismiss: () => {
                setError("Payment cancelled.");
                setPaying(false);
              },
            },
          });
          rzp.open();
        } catch (e) {
          console.error("[razorpay] init/open failed:", e);
          setError("Payment gateway error: " + (e?.message || "Could not open payment window."));
          setPaying(false);
        }
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          logout();
          navigate("/lab-booking/login");
          return;
        }
        console.error("[lab-payment] API error:", err.response?.status, err.response?.data);
        setError(err.response?.data?.message || "Couldn't start payment.");
        setPaying(false);
      });
  };

  const handleRetryRecheck = () => {
    if (!pendingRecheck) return;
    const { pg, rzpResponse } = pendingRecheck;
    const nextRetry = recheckRetries + 1;
    setRecheckRetries(nextRetry);
    setPaying(true);
    setError(null);
    api
      .post("/user/lab/payment-recheck", {
        order_id: pg.booking_id,
        rzp_response: rzpResponse,
        whatsapp_notification: true,
      })
      .then((recheckRes) => {
        if (recheckRes.data?.code === 200) {
          setPendingRecheck(null);
          setCompletedOrder({
            orderId: pg.booking_id,
            amount: calculation?.order_sub_total,
          });
          clearSelection();
          navigate("/lab-booking/confirmed");
        } else {
          setError(
            nextRetry >= 3
              ? `Verification still failing. Please contact support with Order ID: #${pg.booking_id}`
              : "Verification failed again — please retry."
          );
        }
      })
      .catch(() =>
        setError(
          nextRetry >= 3
            ? `Couldn't reach server. Please contact support with Order ID: #${pg.booking_id}`
            : "Network error — please retry."
        )
      )
      .finally(() => setPaying(false));
  };

  const lineItems        = selectedLab?.testList || [];
  const fallbackSubtotal = lineItems.reduce((sum, i) => sum + (Number(i.price) - (Number(i.price) * Number(i.discount || 0)) / 100), 0);
  const fallbackMrp      = lineItems.reduce((sum, i) => sum + Number(i.price), 0);

  // home_collection_charge is already known from Compare Labs — no API needed for display
  const rawCollectionCharge   = selectedLab?.homeCollectionCharge;
  const collectionChargeKnown = rawCollectionCharge !== null && rawCollectionCharge !== undefined;
  const knownCollectionFee    = testCollectionType === 2 && collectionChargeKnown ? Number(rawCollectionCharge) : 0;
  const fallbackTotal         = fallbackSubtotal + knownCollectionFee;
  const fallbackMrpTotal      = fallbackMrp + knownCollectionFee;

  if (!user) {
    return (
      <LabBookingLayout>
        <p className="text-[13px] text-[#6B7A99]">
          Please{" "}
          <button
            onClick={() => navigate("/lab-booking/login")}
            className="text-[#13B58E] font-semibold underline"
          >
            sign in
          </button>{" "}
          to continue.
        </p>
      </LabBookingLayout>
    );
  }

  return (
    <LabBookingLayout>
      {/* pb-28 on mobile so the sticky bottom bar doesn't cover the last slot */}
      <div className="grid grid-cols-12 gap-6 pb-28 sm:pb-0">
        <div className="col-span-12 lg:col-span-8 bg-white rounded-2xl border border-gray-100 p-6">
          <h1 className="text-[18px] font-extrabold text-[#191C1D] mb-4">
            Review Order
          </h1>

          <div className="flex items-center gap-2 mb-5">
            <span className={`inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-full ${
              collectionType === "home" ? "bg-[#EEF3F2] text-[#13B58E]" : "bg-[#EEF3F2] text-[#13B58E]"
            }`}>
              {collectionType === "home" ? "🏠 Home Collection" : "🔬 Lab Visit"}
            </span>
            <button
              onClick={() => navigate("/lab-booking/compare")}
              className="text-[11px] text-[#9AA6A4] underline"
            >
              Change
            </button>
          </div>

          {testCollectionType === 2 && (
            <>
              <h3 className="text-[14px] font-bold text-[#191C1D] mb-2">
                Collection Address
              </h3>
              <div className="flex flex-wrap gap-2 mb-3">
                {addresses.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => setSelectedAddressId(a.id)}
                    className={`text-left text-[12px] px-3.5 py-2 rounded-xl border transition ${
                      selectedAddressId === a.id
                        ? "border-[#13B58E] bg-[#EEF3F2] text-[#191C1D]"
                        : "border-gray-200 text-[#3D4945]"
                    }`}
                  >
                    {a.type}: {a.address_1}, {a.address_2}, {a.pincode}
                  </button>
                ))}
                <button
                  onClick={() => setShowAddressForm((v) => !v)}
                  className="text-[12px] font-semibold px-3.5 py-2 rounded-xl border border-dashed border-[#13B58E] text-[#13B58E]"
                >
                  + Add Address
                </button>
              </div>

              {showAddressForm && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5 border-t border-gray-100 pt-4">
                  <div className="sm:col-span-2">
                    <label className="text-[12px] font-semibold text-[#3D4945]">
                      Flat / House No., Building
                    </label>
                    <input
                      value={addressForm.address_1}
                      onChange={(e) =>
                        setAddressForm((p) => ({ ...p, address_1: e.target.value }))
                      }
                      className="w-full h-11 mt-1.5 px-3.5 rounded-lg border border-gray-200 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#13B58E]"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-[12px] font-semibold text-[#3D4945]">
                      Street / Area / Landmark
                    </label>
                    <input
                      value={addressForm.address_2}
                      onChange={(e) =>
                        setAddressForm((p) => ({ ...p, address_2: e.target.value }))
                      }
                      className="w-full h-11 mt-1.5 px-3.5 rounded-lg border border-gray-200 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#13B58E]"
                    />
                  </div>
                  <div>
                    <label className="text-[12px] font-semibold text-[#3D4945]">
                      City
                    </label>
                    <input
                      value={addressForm.city}
                      onChange={(e) =>
                        setAddressForm((p) => ({ ...p, city: e.target.value }))
                      }
                      className="w-full h-11 mt-1.5 px-3.5 rounded-lg border border-gray-200 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#13B58E]"
                    />
                  </div>
                  <div>
                    <label className="text-[12px] font-semibold text-[#3D4945]">
                      State
                    </label>
                    <input
                      value={addressForm.state}
                      onChange={(e) =>
                        setAddressForm((p) => ({ ...p, state: e.target.value }))
                      }
                      className="w-full h-11 mt-1.5 px-3.5 rounded-lg border border-gray-200 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#13B58E]"
                    />
                  </div>
                  <div>
                    <label className="text-[12px] font-semibold text-[#3D4945]">
                      Pincode
                    </label>
                    <input
                      value={addressForm.pincode}
                      onChange={(e) =>
                        setAddressForm((p) => ({
                          ...p,
                          pincode: e.target.value.replace(/\D/g, ""),
                        }))
                      }
                      maxLength={6}
                      className="w-full h-11 mt-1.5 px-3.5 rounded-lg border border-gray-200 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#13B58E]"
                    />
                  </div>
                  <div>
                    <label className="text-[12px] font-semibold text-[#3D4945]">
                      Type
                    </label>
                    <select
                      value={addressForm.type}
                      onChange={(e) =>
                        setAddressForm((p) => ({ ...p, type: e.target.value }))
                      }
                      className="w-full h-11 mt-1.5 px-3.5 rounded-lg border border-gray-200 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#13B58E]"
                    >
                      <option>Home</option>
                      <option>Work</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <button
                      onClick={handleSaveAddress}
                      disabled={savingAddress}
                      className="bg-[#13B58E] text-white text-[13px] font-semibold px-5 py-2.5 rounded-full hover:bg-[#0fa07d] transition disabled:opacity-60"
                    >
                      {savingAddress ? "Saving..." : "Save Address"}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          <h3 className="text-[14px] font-bold text-[#191C1D] mb-2">
            Preferred Collection Slot
          </h3>
          {loadingSlots && (
            <p className="text-[12px] text-[#6B7A99] mb-3 flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading slots...
            </p>
          )}
          {!loadingSlots && slotDays.length === 0 && (
            <p className="text-[12px] text-[#6B7A99] mb-3">
              No slots available for this lab right now.
            </p>
          )}
          {slotDays.length > 0 && (
            <>
              <div className="flex gap-2 mb-4 overflow-x-auto pb-1 -mx-1 px-1">
                {slotDays.map((d, i) => (
                  <button
                    key={d.date}
                    onClick={() => {
                      setDateIdx(i);
                      setSelectedSlotId(null);
                    }}
                    className={`text-center px-3 py-2 rounded-xl text-[12px] font-semibold transition shrink-0 ${
                      dateIdx === i
                        ? "bg-[#13B58E] text-white"
                        : "border border-gray-200 text-[#3D4945]"
                    }`}
                  >
                    <div>{d.day}</div>
                    <div className="text-[10px] opacity-80">{d.date}</div>
                  </button>
                ))}
              </div>

              <p className="text-[12px] font-semibold text-[#3D4945] mb-3">
                Available Time Slots
              </p>
              <div className="space-y-4 mb-5">
                {groupSlotsByPeriod(selectedDay?.slot || []).map((period) => (
                  <div key={period.key}>
                    <div className="flex items-center gap-1.5 text-[12px] font-semibold text-[#6B7A99] mb-2">
                      <period.icon size={14} />
                      {period.label}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                      {period.slots.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => setSelectedSlotId(s.id)}
                          className={`text-[11px] sm:text-[12px] font-medium px-2 sm:px-3 py-2 rounded-lg transition whitespace-nowrap ${
                            selectedSlotId === s.id
                              ? "bg-[#13B58E] text-white"
                              : "border border-gray-200 text-[#3D4945]"
                          }`}
                        >
                          {formatTime12h(s.from_time)} - {formatTime12h(s.to_time)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {error && (
            <div className="mb-4">
              <p className="text-[12px] text-red-500">{error}</p>
              {pendingRecheck && recheckRetries < 3 && (
                <button
                  onClick={handleRetryRecheck}
                  disabled={paying}
                  className="mt-2 text-[12px] font-semibold text-white bg-red-500 hover:bg-red-600 px-4 py-2 rounded-full transition disabled:opacity-60"
                >
                  {paying ? "Verifying..." : "Retry Payment Verification"}
                </button>
              )}
            </div>
          )}

          <button
            onClick={() => navigate(-1)}
            className="px-5 py-2.5 rounded-full border border-gray-200 text-[#3D4945] text-[13px] font-semibold"
          >
            Back
          </button>

          <div className="bg-[#0F172A] rounded-2xl p-4 mt-5">
            <div className="flex items-start gap-3">
              <Smartphone className="text-white shrink-0 hidden sm:block" size={22} />
              <div className="flex-1 min-w-0">
                <p className="text-white text-[12px] font-semibold">
                  Download HealthismPlus App to View Your Reports
                </p>
                <p className="text-white/60 text-[11px] mt-0.5">
                  Lab reports will be available in the app within 24-48 hrs of sample collection.
                </p>
              </div>
            </div>
            <a
              href="https://onelink.to/r6c4yw"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 w-full block text-center bg-[#13B58E] text-white text-[12px] font-semibold px-4 py-2 rounded-full sm:hidden"
            >
              Download App
            </a>
            <a
              href="https://onelink.to/r6c4yw"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex mt-3 bg-[#13B58E] text-white text-[12px] font-semibold px-4 py-2 rounded-full"
            >
              Download App
            </a>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-4">
          <div className="bg-white rounded-2xl border border-gray-100 p-5 sticky top-24">
            <h3 className="text-[14px] font-bold text-[#191C1D] mb-3">
              Order Summary
            </h3>
            {calculation ? (
              <div className="space-y-2 text-[13px]">
                {(calculation.charges || []).map((c) => (
                  <div key={c.title} className="flex justify-between">
                    <span className="text-[#3D4945]">{c.title}</span>
                    <span
                      className="font-medium"
                      style={{ color: c.color || "#191C1D" }}
                    >
                      {c.symbol}₹{formatPrice(c.value)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-3 text-[13px]">
                {lineItems.map((item) => {
                  const mrp        = Number(item.price);
                  const finalPrice = mrp - (mrp * Number(item.discount || 0)) / 100;
                  const hasDiscount = Number(item.discount) > 0;
                  return (
                    <div key={item.id}>
                      <span className="text-[#3D4945]">{item.name}</span>
                      <div className="flex items-baseline gap-1.5 mt-0.5">
                        {hasDiscount && (
                          <span className="text-[11px] text-[#9AA6A4] line-through">₹{formatPrice(mrp)}</span>
                        )}
                        <span className="text-[#191C1D] font-semibold">₹{formatPrice(finalPrice)}</span>
                        {hasDiscount && (
                          <span className="text-[10px] text-[#13B58E] font-semibold">
                            ({Math.round(Number(item.discount))}% off)
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {testCollectionType === 2 && (
                  <div className="flex justify-between text-[12px] text-[#9AA6A4]">
                    <span>Home Collection</span>
                    <span className={knownCollectionFee === 0 && collectionChargeKnown ? "text-[#13B58E] font-semibold" : ""}>
                      {!collectionChargeKnown ? "—" : knownCollectionFee > 0 ? `+₹${formatPrice(knownCollectionFee)}` : "Free"}
                    </span>
                  </div>
                )}
              </div>
            )}
            <div className="border-t border-gray-100 mt-3 pt-3 flex justify-between items-end">
              <span className="text-[14px] font-bold text-[#191C1D]">Total Amount</span>
              <div className="text-right">
                {!calculation && fallbackMrpTotal > fallbackTotal && (
                  <p className="text-[11px] text-[#9AA6A4] line-through">₹{formatPrice(fallbackMrpTotal)}</p>
                )}
                <span className="text-[16px] font-extrabold text-[#191C1D]">
                  ₹{formatPrice(calculation?.order_sub_total ?? fallbackTotal)}
                </span>
              </div>
            </div>
            {!canCheckout && (
              <p className="text-[11px] text-[#B45309] mt-3">
                {!patient && "Select a patient. "}
                {testCollectionType === 2 && !selectedAddressId && "Add a collection address. "}
                {!selectedSlotId && "Select a time slot. "}
              </p>
            )}
            {/* Pay button — hidden on mobile, sticky bar handles it there */}
            <div className="hidden sm:block">
              {!orderRefId ? (
                <button
                  onClick={handleCheckout}
                  disabled={!canCheckout || checking}
                  className="w-full bg-[#13B58E] text-white text-[14px] font-semibold py-3 rounded-full mt-4 hover:bg-[#0fa07d] transition disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {checking ? (
                    <><Loader2 size={14} className="animate-spin" /> Calculating...</>
                  ) : canCheckout ? (
                    `Pay ₹${formatPrice(fallbackTotal)}`
                  ) : (
                    "Select a slot to pay"
                  )}
                </button>
              ) : (
                <button
                  onClick={handlePay}
                  disabled={paying}
                  className="w-full bg-[#13B58E] text-white text-[14px] font-semibold py-3 rounded-full mt-4 hover:bg-[#0fa07d] transition disabled:opacity-60"
                >
                  {paying
                    ? "Processing..."
                    : `Pay ₹${formatPrice(calculation?.order_sub_total ?? fallbackTotal)}`}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile-only sticky Pay bar — desktop uses the sidebar button */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-t border-gray-100 px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[12px] text-[#6B7A99]">Total Amount</span>
          <span className="text-[15px] font-extrabold text-[#191C1D]">
            ₹{formatPrice(calculation?.order_sub_total ?? fallbackTotal)}
          </span>
        </div>
        {!orderRefId ? (
          <button
            onClick={handleCheckout}
            disabled={!canCheckout || checking}
            className="w-full bg-[#13B58E] text-white text-[14px] font-semibold py-3 rounded-full hover:bg-[#0fa07d] transition disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {checking ? (
              <><Loader2 size={14} className="animate-spin" /> Calculating...</>
            ) : canCheckout ? (
              `Pay ₹${formatPrice(fallbackTotal)}`
            ) : (
              "Select a slot to pay"
            )}
          </button>
        ) : (
          <button
            onClick={handlePay}
            disabled={paying}
            className="w-full bg-[#13B58E] text-white text-[14px] font-semibold py-3 rounded-full hover:bg-[#0fa07d] transition disabled:opacity-60"
          >
            {paying
              ? "Processing..."
              : `Pay ₹${formatPrice(calculation?.order_sub_total ?? fallbackTotal)}`}
          </button>
        )}
      </div>
    </LabBookingLayout>
  );
};

export default ReviewOrderPage;
